import {
  Button,
  Group,
  MultiSelect,
  Text,
  TextInput,
  Textarea,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconCalendarEvent,
  IconCheck,
  IconClock,
  IconPencil,
  IconUser,
  IconUserMinus,
  IconUserPlus,
} from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { updateCalendarEvent } from "api";
import { useUser } from "stores/useUser";
import { useAssistantWidgetStatus } from "stores/useAssistantWidgetStatus";
import {
  EMAIL_PATTERN,
  getTeamOptions,
  normalizeEmail,
  notify,
  uniqueEmails,
} from "utils/common";
import {
  formatTimeRange,
  fromLocalInputValue,
  resolveWidgetTimezone,
  toLocalInputValue,
} from "./calendarWidgetTime";
import {
  descriptionHtmlToText,
  descriptionToHtml,
} from "./calendarWidgetDescription";
import useContactDirectory from "hooks/useContactDirectory";
import {
  createDirectoryOptions,
  filterRecipient,
  RecipientOption,
} from "./recipientPicker";
import css from "./MicChatDrawer.module.scss";

const normalizeAttendeeOptions = (attendees) =>
  (Array.isArray(attendees) ? attendees : [])
    .map((attendee) => ({
      value: attendee.email,
      label: attendee.displayName || attendee.email,
    }))
    .filter((attendee) => attendee.value);

const mergeOptions = (...optionLists) => {
  const seen = new Set();
  return optionLists.flat().filter((option) => {
    const value =
      typeof option?.value === "string" ? option.value.trim().toLowerCase() : "";
    if (!value || seen.has(value)) return false;
    seen.add(value);
    return true;
  });
};

const getUnresolvedAttendeeInput = (attendee) =>
  normalizeEmail(attendee?.email || attendee?.input);

const CalendarEventUpdateWidget = ({
  update,
  statusKey,
  portalZIndex,
}) => {
  const { user } = useUser();
  const persistedStatus = useAssistantWidgetStatus(
    (state) => state.statusByKey[statusKey],
  );
  const markStatus = useAssistantWidgetStatus((state) => state.markStatus);
  const tz = resolveWidgetTimezone(update.timezone);
  const current = update.current || {};
  const proposed = update.proposed || {};

  const [title, setTitle] = useState(proposed.title || "Untitled meeting");
  const [description, setDescription] = useState(
    descriptionHtmlToText(proposed.description || ""),
  );
  const [startDateTimeIso, setStartDateTimeIso] = useState(
    proposed.startDateTimeIso,
  );
  const [endDateTimeIso, setEndDateTimeIso] = useState(proposed.endDateTimeIso);
  const [attendeeEmails, setAttendeeEmails] = useState(
    uniqueEmails((proposed.attendees || []).map((attendee) => attendee.email)),
  );
  const [isConfirming, setIsConfirming] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(persistedStatus === "confirmed");
  const [isDismissed, setIsDismissed] = useState(persistedStatus === "dismissed");

  const directory = useContactDirectory();
  const options = useMemo(
    () =>
      mergeOptions(
        normalizeAttendeeOptions(current.attendees),
        normalizeAttendeeOptions(proposed.attendees),
        createDirectoryOptions(directory),
        getTeamOptions(),
        attendeeEmails.map((email) => ({ value: email, label: email })),
      ),
    [attendeeEmails, current.attendees, directory, proposed.attendees],
  );

  const currentEmails = useMemo(
    () => new Set(uniqueEmails((current.attendees || []).map((a) => a.email))),
    [current.attendees],
  );
  const currentDescriptionText = useMemo(
    () => descriptionHtmlToText(current.description || "").trim(),
    [current.description],
  );

  // Live diff between the original event (current) and the edited state, so the
  // summary keeps up as the user tweaks the widget before confirming.
  const addedAttendees = uniqueEmails(attendeeEmails).filter(
    (email) => !currentEmails.has(email),
  );
  const removedAttendees = [...currentEmails].filter(
    (email) => !attendeeEmails.some((value) => normalizeEmail(value) === email),
  );
  const titleChanged = title.trim() !== (current.title || "").trim();
  const timeChanged =
    startDateTimeIso !== current.startDateTimeIso ||
    endDateTimeIso !== current.endDateTimeIso;
  const descriptionChanged = description.trim() !== currentDescriptionText;
  const hasChanges =
    titleChanged ||
    timeChanged ||
    descriptionChanged ||
    addedAttendees.length > 0 ||
    removedAttendees.length > 0;

  // Mirror the draft widget: each manually-added email clears one unresolved
  // entry off the front, since the agent could not map those names to emails.
  const originalAttendeeEmails = new Set(
    uniqueEmails((proposed.attendees || []).map((attendee) => attendee.email)),
  );
  const addedAttendeeCount = uniqueEmails(attendeeEmails).filter(
    (email) => !originalAttendeeEmails.has(email),
  ).length;
  const exactUnresolvedAttendees = [
    ...(update.unresolvedAttendees || []),
    ...(update.ambiguousAttendees || []),
  ].filter((attendee) => {
    const unresolvedInput = getUnresolvedAttendeeInput(attendee);
    return (
      !unresolvedInput ||
      !attendeeEmails.some((email) => normalizeEmail(email) === unresolvedInput)
    );
  });
  const unresolvedAttendees = exactUnresolvedAttendees.slice(addedAttendeeCount);
  const unresolvedCount = unresolvedAttendees.length;

  const hasValidTimes = Boolean(
    fromLocalInputValue(toLocalInputValue(startDateTimeIso, tz), tz) &&
      fromLocalInputValue(toLocalInputValue(endDateTimeIso, tz), tz) &&
      new Date(endDateTimeIso).getTime() > new Date(startDateTimeIso).getTime(),
  );
  const canConfirm =
    !isConfirmed &&
    !isDismissed &&
    !isConfirming &&
    Boolean(title.trim()) &&
    hasValidTimes &&
    unresolvedCount === 0 &&
    hasChanges;

  const statusCopy = useMemo(() => {
    if (isConfirmed) return "Event updated";
    if (isDismissed) return "Left unchanged";
    if (!hasChanges) return "No changes yet";
    if (unresolvedCount > 0) return "Needs attendee details";
    if (!update.requestedSlotAvailable) return "New time is busy";
    return "Ready to confirm";
  }, [
    hasChanges,
    isConfirmed,
    isDismissed,
    unresolvedCount,
    update.requestedSlotAvailable,
  ]);

  const handleSelectSuggestion = (slot) => {
    setStartDateTimeIso(slot.startDateTimeIso);
    setEndDateTimeIso(slot.endDateTimeIso);
  };

  const handleUpdate = async () => {
    if (!canConfirm) return;

    const userEmail = normalizeEmail(user?.email);
    const finalAttendees = uniqueEmails(attendeeEmails).filter(
      (email) => email !== userEmail,
    );

    setIsConfirming(true);
    try {
      await updateCalendarEvent(update.eventId, {
        summary: title.trim(),
        description: descriptionToHtml(description),
        start: { dateTime: startDateTimeIso, timeZone: tz },
        end: { dateTime: endDateTimeIso, timeZone: tz },
        attendees: finalAttendees.map((email) => ({ email })),
      });
      setIsConfirmed(true);
      markStatus(statusKey, "confirmed");
      notify("Event updated successfully!", "success");
    } catch (error) {
      notify(error.message || "Failed to update event", "error");
    } finally {
      setIsConfirming(false);
    }
  };

  const conflicts = Array.isArray(update.conflicts) ? update.conflicts : [];
  const suggestedSlots = Array.isArray(update.suggestedSlots)
    ? update.suggestedSlots
    : [];
  // Names the agent could not map to a roster member for removal — the user
  // has to take them off the attendee list manually below.
  const unresolvedRemoveAttendees = (
    Array.isArray(update.unresolvedRemoveAttendees)
      ? update.unresolvedRemoveAttendees
      : []
  ).filter((attendee) => {
    const input = normalizeEmail(attendee?.input);
    return !input || attendeeEmails.every((email) => normalizeEmail(email) !== input);
  });
  const showChanges = hasChanges && !isConfirmed && !isDismissed;

  return (
    <div className={css.calendarDraftCard}>
      <Group position="apart" align="flex-start" spacing="sm" noWrap>
        <div className={css.calendarDraftTitleGroup}>
          <span
            className={`${css.calendarDraftStatusIcon} ${
              isConfirmed || (hasChanges && unresolvedCount === 0 && update.requestedSlotAvailable)
                ? css.calendarDraftStatusReady
                : css.calendarDraftStatusNeedsWork
            }`}
          >
            {isConfirmed ? <IconCheck size={14} /> : <IconAlertTriangle size={14} />}
          </span>
          <div>
            <Text weight={800} size="sm" color="#253047">
              Update event
            </Text>
            <Text size="xs" color="#667085">
              {statusCopy}
            </Text>
          </div>
        </div>
        {isConfirmed && <span className={css.calendarDraftPill}>Updated</span>}
      </Group>

      {showChanges && (
        <div className={css.calendarUpdateChanges}>
          {titleChanged && (
            <div className={css.calendarUpdateChangeRow}>
              <IconPencil size={13} />
              <span>
                <span className={css.calendarUpdateOld}>
                  {current.title || "Untitled meeting"}
                </span>
                <span className={css.calendarUpdateArrow}>→</span>
                {title.trim() || "Untitled meeting"}
              </span>
            </div>
          )}
          {timeChanged && (
            <div className={css.calendarUpdateChangeRow}>
              <IconClock size={13} />
              <span>
                <span className={css.calendarUpdateOld}>
                  {formatTimeRange(
                    {
                      startDateTimeIso: current.startDateTimeIso,
                      endDateTimeIso: current.endDateTimeIso,
                    },
                    tz,
                  )}
                </span>
                <span className={css.calendarUpdateArrow}>→</span>
                {formatTimeRange({ startDateTimeIso, endDateTimeIso }, tz)}
              </span>
            </div>
          )}
          {descriptionChanged && (
            <div className={css.calendarUpdateChangeRow}>
              <IconCalendarEvent size={13} />
              <span>Description updated</span>
            </div>
          )}
          {addedAttendees.map((email) => (
            <div key={`add-${email}`} className={css.calendarUpdateChangeRow}>
              <IconUserPlus size={13} />
              <span className={css.calendarUpdateAdd}>Add {email}</span>
            </div>
          ))}
          {removedAttendees.map((email) => (
            <div key={`remove-${email}`} className={css.calendarUpdateChangeRow}>
              <IconUserMinus size={13} />
              <span className={css.calendarUpdateRemove}>Remove {email}</span>
            </div>
          ))}
        </div>
      )}

      <div className={css.calendarDraftFields}>
        <TextInput
          label="Title"
          value={title}
          onChange={(event) => setTitle(event.currentTarget.value)}
          classNames={{
            root: css.calendarDraftInputRoot,
            input: css.calendarDraftInput,
            label: css.calendarDraftLabel,
          }}
        />

        <div className={css.calendarDraftTimeGrid}>
          <label className={css.calendarDraftNativeField}>
            <span>Start</span>
            <input
              type="datetime-local"
              value={toLocalInputValue(startDateTimeIso, tz)}
              onChange={(event) => {
                const nextIso = fromLocalInputValue(event.currentTarget.value, tz);
                if (nextIso) setStartDateTimeIso(nextIso);
              }}
            />
          </label>
          <label className={css.calendarDraftNativeField}>
            <span>End</span>
            <input
              type="datetime-local"
              value={toLocalInputValue(endDateTimeIso, tz)}
              onChange={(event) => {
                const nextIso = fromLocalInputValue(event.currentTarget.value, tz);
                if (nextIso) setEndDateTimeIso(nextIso);
              }}
            />
          </label>
        </div>

        <MultiSelect
          zIndex={portalZIndex}
          label="Attendees"
          data={options}
          value={attendeeEmails}
          onChange={(emails) => setAttendeeEmails(uniqueEmails(emails))}
          searchable
          creatable
          filter={filterRecipient}
          itemComponent={RecipientOption}
          limit={8}
          getCreateLabel={(query) =>
            EMAIL_PATTERN.test(query) ? `Add ${query}` : "Enter an email"
          }
          onCreate={(query) => {
            const value = query.trim().toLowerCase();
            if (!EMAIL_PATTERN.test(value)) return null;
            return { value, label: value };
          }}
          placeholder="Add people"
          classNames={{
            root: css.calendarDraftInputRoot,
            input: css.calendarDraftInput,
            label: css.calendarDraftLabel,
          }}
        />

        <Textarea
          label="Description"
          value={description}
          onChange={(event) => setDescription(event.currentTarget.value)}
          placeholder="Add agenda or notes"
          minRows={3}
          autosize
          classNames={{
            root: css.calendarDraftInputRoot,
            input: css.calendarDraftTextarea,
            label: css.calendarDraftLabel,
          }}
        />
      </div>

      {!hasValidTimes && (
        <Text size="xs" color="red" className={css.calendarDraftNotice}>
          Choose an end time after the start time.
        </Text>
      )}

      {unresolvedCount > 0 && (
        <div className={css.calendarDraftNotice}>
          {unresolvedAttendees.map((attendee) => (
            <div key={attendee.input} className={css.calendarDraftWarningRow}>
              <IconUser size={13} />
              <span>
                {attendee.input}:{" "}
                {attendee.candidates?.length
                  ? "pick the exact person or add their email"
                  : "email needed"}
              </span>
            </div>
          ))}
        </div>
      )}

      {unresolvedRemoveAttendees.length > 0 && (
        <div className={css.calendarDraftNotice}>
          {unresolvedRemoveAttendees.map((attendee) => (
            <div key={attendee.input} className={css.calendarDraftWarningRow}>
              <IconUserMinus size={13} />
              <span>
                Couldn&apos;t find {attendee.input} to remove — take them off the
                attendee list above if needed.
              </span>
            </div>
          ))}
        </div>
      )}

      {conflicts.length > 0 && (
        <div className={css.calendarDraftNotice}>
          {conflicts.slice(0, 3).map((conflict) => (
            <div
              key={`${conflict.email}-${conflict.startDateTimeIso}`}
              className={css.calendarDraftWarningRow}
            >
              <IconClock size={13} />
              <span>
                {conflict.displayName || conflict.email} is busy{" "}
                {formatTimeRange(conflict, tz)}
              </span>
            </div>
          ))}
        </div>
      )}

      {suggestedSlots.length > 0 && (
        <div className={css.calendarDraftSuggestions}>
          <Text className={css.calendarDraftLabel}>Suggested slots</Text>
          <div className={css.calendarDraftSuggestionList}>
            {suggestedSlots.map((slot) => (
              <button
                type="button"
                key={`${slot.startDateTimeIso}-${slot.endDateTimeIso}`}
                className={css.calendarDraftSuggestion}
                onClick={() => handleSelectSuggestion(slot)}
              >
                {formatTimeRange(slot, tz)}
              </button>
            ))}
          </div>
        </div>
      )}

      <Group position="apart" spacing="xs" className={css.calendarDraftFooter}>
        <Text size="xs" color="#667085" className={css.calendarDraftCurrentTime}>
          {formatTimeRange({ startDateTimeIso, endDateTimeIso }, tz)}
        </Text>
        <Group spacing={6} noWrap>
          <Button
            compact
            variant="default"
            disabled={isConfirming || isConfirmed || isDismissed}
            onClick={() => {
              setIsDismissed(true);
              markStatus(statusKey, "dismissed");
            }}
          >
            Keep as-is
          </Button>
          <Button
            compact
            leftIcon={<IconCalendarEvent size={14} />}
            disabled={!canConfirm}
            loading={isConfirming}
            onClick={handleUpdate}
            className={css.calendarDraftConfirm}
          >
            Confirm update
          </Button>
        </Group>
      </Group>
    </div>
  );
};

export default CalendarEventUpdateWidget;
